import { useAuth } from "hooks/Auth";
import { useNotification } from "hooks/Notification";
import React, {
    useEffect,
    useCallback,
    useState,
    useContext,
    useRef,
    Fragment,
} from "react";
import Container from "Screens/Container";
import { oauthPut, oauthGet, oauthPost, oauthDel } from "helpers/Request";
import { transform as userTransformer } from "schemas/User";
import { transform as roleTransformer } from "schemas/Role";
import { getPaginationMeta } from "schemas/Util";
import UserTable from "./UserTable";
import { AppContext } from "context/App";
import { useModal } from "hooks/Modal";
import Pagination from "components/Pagination";
import { Link, useLocation, useHistory } from "react-router-dom";
import PrimaryButton from "components/Buttons/PrimaryButton";
import LowTextInput from "components/FormElements/LowTextInput";
import InviteUsers from "./InviteUsers";
import AdminScreen from "Screens/AdminScreen";
import { Formik, Form } from "formik";
import AutocompleteInput from "components/FormElements/AutocompleteInput";
import UserLoadingRow from "./UserLoadingRow";

export default function UserScreen(props) {
    const auth = useAuth();
    const notification = useNotification();
    const modal = useModal();
    const location = useLocation();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [pagination, setPagination] = useState(null);
    const formRef = useRef();

    const { setCurrentNav } = useContext(AppContext);

    const updateParams = (vals) => {
        const params = new URLSearchParams(location.search);

        Object.entries(vals).forEach(([k, v]) => {
            if (v === null) {
                params.delete(k);
                return;
            }
            params.set(k, v);
        });

        history.replace(location.pathname + "?" + params.toString());
    };

    const loadUsers = useCallback(() => {
        const defaultParams = { page: 1 };
        const definedParams = Object.fromEntries(
            new URLSearchParams(location.search)
        );
        setUsers([]);
        setSelectedItems([]);
        setLoading(true);

        oauthGet(
            "/api/v2/user",
            Object.assign(defaultParams, definedParams, {
                license: auth.publication.uuid,
            })
        ).then((data) => {
            setUsers(userTransformer(data));
            setPagination(getPaginationMeta(data));
            setLoading(false);
        });
    }, [auth.publication, location]);

    const onRequestMagic = function (uuid, e) {
        modal.show({
            title: "Magic Link",
            confirmText: "Send Link",
            children: (
                <p className="text-sm text-gray-800">
                    Send this user a magic link to reset their password?
                </p>
            ),
            onRequestClose: (closeModal) => {
                modal.set({
                    confirmText: "Sending...",
                });
                oauthPost(`/api/v2/user/${uuid}/magic`)
                    .then((data) => {
                        notification.show("Magic Link Sent");
                    })
                    .catch((e) => {
                        notification.show(
                            "There was an error sending. Please try again."
                        );
                    })
                    .finally(() => {
                        closeModal();
                    });
            },
        });
    };

    const onUpdate = (values) => {
        const roles = values.roles.map((item) => item.value);
        const promises = selectedItems.map((item) =>
            oauthPut(`/api/v2/user/${item}`, { roles, append_roles: true })
        );
        return Promise.all(promises).then(() =>
            notification.show("Users updated successfully")
        );
    };

    const selectedUpdateRoles = () => {
        modal.show({
            title: "Add users to groups",
            confirmText: "Update users",
            children: (
                <Formik
                    initialValues={{ roles: [] }}
                    onSubmit={onUpdate}
                    innerRef={formRef}
                >
                    <Form>
                        <AutocompleteInput
                            label="Groups"
                            id="roles"
                            name="roles"
                            helpertext={
                                <Fragment>
                                    Showing groups for {auth.publication.name},
                                    only.
                                </Fragment>
                            }
                            onRequestSearch={async (term) => {
                                let items = await oauthGet("/api/v2/role", {
                                    keyword: term,
                                    license: auth.publication.uuid,
                                });
                                return roleTransformer(items).map((item) => {
                                    return {
                                        label: item.name,
                                        value: item.id,
                                    };
                                });
                            }}
                        />
                    </Form>
                </Formik>
            ),
            onRequestClose: (next) => {
                modal.set({
                    confirmText: "Saving...",
                });
                formRef.current.submitForm().then(() => next());
            },
        });
    };

    const selectedSendMagicLink = () => {
        modal.show({
            title: "Magic Link",
            confirmText: "Send Link",
            children: (
                <p className="text-sm text-gray-800">
                    Send this user a magic login link to selected users?
                </p>
            ),
            onRequestClose: (closeModal) => {
                modal.set({
                    confirmText: "Sending...",
                });
                const promises = selectedItems.map((item) =>
                    oauthPost(`/api/v2/user/${item}/magic`)
                );

                Promise.all(promises)
                    .then((data) => {
                        notification.show("Magic Link Sent");
                    })
                    .catch((e) => {
                        notification.show(
                            "There was an error sending. Please try again."
                        );
                    })
                    .finally(() => {
                        closeModal();
                    });
            },
        });
    };

    const selectedDeactivateUsers = () => {
        modal.show({
            title: "Deactivate Users",
            confirmText: "Deactivate",
            children: (
                <p className="text-sm text-gray-800">
                    This will deactivate the selected users. Are you sure?
                </p>
            ),
            onRequestClose: (closeModal) => {
                modal.set({
                    confirmText: "Deactivating...",
                });
                const promises = selectedItems.map((item) =>
                    oauthPut(`/api/v2/user/${item}`, { is_active: false })
                );

                Promise.all(promises)
                    .then((data) => {
                        notification.show("Users deactivated");
                    })
                    .catch((e) => {
                        notification.show(
                            "There was an updating. Please try again."
                        );
                    })
                    .finally(() => {
                        loadUsers();
                        closeModal();
                    });
            },
        });
    };

    const selectedDeleteUsers = () => {
        modal.show({
            title: "Delete Users",
            confirmText: "Delete",
            children: (
                <p className="text-sm text-gray-800">
                    This will remove the selected users. You can deactivate them
                    if you want to keep their data. Are you sure?
                </p>
            ),
            onRequestClose: (closeModal) => {
                modal.set({
                    confirmText: "Deleting...",
                });
                const promises = selectedItems.map((item) =>
                    oauthDel(`/api/v2/user/${item}`)
                );

                Promise.all(promises)
                    .then((data) => {
                        notification.show("Users Deleted");
                    })
                    .catch((e) => {
                        notification.show(
                            "There was an updating. Please try again."
                        );
                    })
                    .finally(() => {
                        loadUsers();
                        closeModal();
                    });
            },
        });
    };

    const onRequestUpdate = (what) => {
        switch (what) {
            case "roles":
                selectedUpdateRoles();
                break;
            case "magic":
                selectedSendMagicLink();
                break;
            case "status":
                selectedDeactivateUsers();
                break;
            case "delete":
                selectedDeleteUsers();
                break;
            default:
                break;
        }
    };

    // show the confirm overlay,
    const onRequestDelete = function (uuid, e) {
        if (uuid === auth.user.uuid) {
            notification.show("You cannot delete yourself.");
            return;
        }

        modal.show({
            variant: "danger",
            title: "Delete User",
            confirmText: "Yes, delete user",
            children: (
                <p className="text-sm text-gray-800">
                    Are you sure you want to delete this user? This cannot be
                    undone.
                </p>
            ),
            onRequestClose: (closeModal) => {
                modal.set({
                    confirmText: "Deleting...",
                });
                oauthDel(`/api/v2/user/${uuid}`)
                    .then((data) => {
                        notification.show("User removed");
                        setTimeout(loadUsers, 1);
                    })
                    .catch((e) => {
                        notification.show(
                            "There was an error removing. Please try again."
                        );
                    })
                    .finally(() => {
                        closeModal();
                    });
            },
        });
    };

    const setSelected = (e, id) => {
        if (e.target.checked) {
            setSelectedItems([...selectedItems, id]);
        } else {
            setSelectedItems(selectedItems.filter((item) => item !== id));
        }
    };

    useEffect(() => {
        loadUsers();
    }, [loadUsers, location]);

    useEffect(() => {
        setCurrentNav("user");
    }, [setCurrentNav]);

    return (
        <AdminScreen>
            <Container>
                <div className="space-y-4">
                    <div className="flex space-x-4 items-center">
                        <div className="grow">
                            <LowTextInput
                                placeholder="Search Users"
                                onEnter={(val) => {
                                    updateParams({
                                        page: 1,
                                        keyword: val || null,
                                    });
                                }}
                            />
                        </div>

                        <div className="space-x-4">
                            <PrimaryButton
                                type="button"
                                as={Link}
                                to="/app/user/new"
                            >
                                New User
                            </PrimaryButton>
                            <InviteUsers />
                        </div>
                    </div>

                    <div>
                        <UserTable
                            users={users}
                            onRequestMagic={onRequestMagic}
                            onRequestDelete={onRequestDelete}
                            onRequestUpdate={onRequestUpdate}
                            selectedItems={selectedItems}
                            addSelected={setSelected}
                            setAllSelected={setSelectedItems}
                        />
                        {loading && <UserLoadingRow rows={10} />}
                    </div>
                    <Pagination {...pagination} basePath="/app/user" />
                </div>
            </Container>
        </AdminScreen>
    );
}
